import { ActionContext as VuexActionContext, Store } from 'vuex'
import { Namespace } from '@/store/Namespaces'
import { parseExcelSheetAddresses, validateAddresses } from '@/services/landlord.service'
import { ParsedAddress } from '@/types/landlord.types'
import {
  PropertyForm,
  ParsedAddressSpreadsheet,
  ParsedAddressSpreadsheetData,
  PropertyFormFields,
  FormId
} from '@/types/landlord-order.types'

import { ADDRESS_LIMIT, SHEET_RANGE } from '@/constants/landlord/order-property.ts'
import {
  FormFields,
  Attachment
} from '@/types/landlord-order.types'

export const namespace = (fragment: string) => `${Namespace.LandlordOrder}/${Namespace.LandlordOrderProperty}/${fragment}`

export enum Property {
  Forms = 'forms',
  Attachments = 'attachments',
  ValidationIsOngoing = 'validationIsOngoing',
  IsFormDirty = 'isFormDirty'
}

interface State {
  [Property.Forms]: PropertyForm[];
  [Property.Attachments]: Attachment[];
  [Property.ValidationIsOngoing]: boolean;
  [Property.IsFormDirty]: boolean;
}

export enum Getter {
  AllFieldsAreValid = 'allFieldsAreValid',
  VisibleFormCount = 'visibleFormCount',
  GetField = 'getField',
  GetEmptyAttachment = 'getEmptyAttachment',
  Attachment = 'attachment',
  AttachmentFiles = 'attachmentFiles',
  GetInvalidAttachment = 'getInvalidAttachment',
  GetTooManyAddresses = 'getTooManyAddresses',
  GetCorruptAttachment = 'getCorruptAttachment',
  GetLoadingAttachmentIndexes = 'getLoadingAttachmentIndexes',
  GetAttachmentWithTooManyAddresses = 'getAttachmentWithTooManyAddresses'
}

export enum Setter {
  UpdateField = 'updateField',
  UpdateIsFormDirty = 'updateIsFormDirty',
  CreateForm = 'createForm',
  SetFormValid = 'setFormValid',
  CopyFormValues = 'copyFormValues',
  RemoveForms = 'removeForms',
  SetValidationIsOngoing = 'setValidationIsOngoing',
  AddAttachment = 'addAttachment',
  RemoveAttachment = 'removeAttachment',
  SetAddresses = 'setAddresses',
  SetAttachmentEmpty = 'setAttachmentEmpty',
  SetAttachmentBeingValidated = 'setAttachmentBeingValidated',
  SetAttachmentTooManyAddresses = 'setAttachmentTooManyAddresses',
  SetInvalidRows = 'setInvalidRows',
  SetAttachmentCorrupt = 'setAttachmentCorrupt'
}

export enum Action {
  UpdateField = 'updateField',
  SetFormValid = 'setFormValid',
  CreateForm = 'createForm',
  RemoveForms = 'removeForms',
  CopyFormValues = 'copyFormValues',
  ResetPropertyForm = 'resetPropertyForm',
  SetValidationIsOngoing = 'setValidationIsOngoing',
  AddAttachment = 'addAttachment',
  RemoveAttachment = 'removeAttachment',
  ParseAddresses = 'parseAddresses',
  ValidateParsedAttachment = 'validateParsedAttachment'
}

export type ActionContext = VuexActionContext<State, {}>

const isAttachmentEmpty = (attachment: ParsedAddressSpreadsheet) => !attachment.addresses.length
const getSortedInvalidRows = (invalidAddresses: ParsedAddressSpreadsheetData[]) => {
  const invalidRows = invalidAddresses.reduce((accumulator, address) => {
    const row = ++address[ParsedAddress.RowIndex]
    if (!accumulator.includes(row)) {
      accumulator.push(row)
    }

    return accumulator
  }, [] as number[])

  return invalidRows.sort((a, b) => a > b ? 1 : -1)
}
const tooManyAddresses = (attachment: ParsedAddressSpreadsheet) => attachment.addresses.find(address => address[ParsedAddress.RowIndex] === 102)
const requiredFieldsValidator = (address: ParsedAddressSpreadsheetData) => address[ParsedAddress.Street].trim() === ''
  || address[ParsedAddress.StreetNumber].trim() === ''
  || address[ParsedAddress.ApartmentNumber].trim() === ''
  || address[ParsedAddress.City].trim() === ''
const validateRequiredFields = (addresses: ParsedAddressSpreadsheetData[]): ParsedAddressSpreadsheetData[] => {
  return addresses.filter(address => requiredFieldsValidator(address))
}
const findIndexByAttachmentName = (state: State, name: string): number => {
  return state[Property.Attachments].findIndex(attachment => attachment.file.name === name)
}
const findForm = (state: State, formId: FormId): PropertyForm => {
  return state[Property.Forms].find((form: PropertyForm) => form.id === formId) as PropertyForm
}

export const addForm = (store: Store<State>, formId: FormId): Promise<void> => store.dispatch(namespace(Action.CreateForm), formId)
export const removeForms = (store: Store<State>, formId: FormId): Promise<void> => store.dispatch(namespace(Action.RemoveForms), formId)
export const copyFormValues = (store: Store<State>, from: FormId, to: FormId): Promise<void> => store.dispatch(namespace(Action.CopyFormValues), { from, to })
export const updateField = (store: Store<State>, formId: FormId, fieldName: keyof FormFields, value: string): Promise<void> => store.dispatch(namespace(Action.UpdateField), { formId, fieldName, value })
export const setFormValid = (store: Store<State>, formId: FormId, valid: boolean): Promise<void> => store.dispatch(namespace(Action.SetFormValid), { formId, valid })
export const setValidationIsOngoing = (store: Store<State>, val: boolean): Promise<void> => store.dispatch(namespace(Action.SetValidationIsOngoing), val)
export const resetPropertyForm = (store: Store<State>): Promise<void> => store.dispatch(namespace(Action.ResetPropertyForm))
export const addAttachment = (store: Store<State>, attachment: File): Promise<void> => store.dispatch(namespace(Action.AddAttachment), attachment)
export const removeAttachment = (store: Store<State>, attachmentName: string): Promise<void> => store.dispatch(namespace(Action.RemoveAttachment), attachmentName)

export const getIsFormDirty = (store: Store<State>): boolean => store.getters[namespace(Property.IsFormDirty)]
export const getForms = (store: Store<State>): PropertyForm[] => store.getters[namespace(Property.Forms)]
export const getVisibleFormCount = (store: Store<State>): number => store.getters[namespace(Getter.VisibleFormCount)]
export const getAllFormsAreValid = (store: Store<State>): boolean => store.getters[namespace(Getter.AllFieldsAreValid)]
export const getAttachments = (store: Store<State>): Attachment[] => store.getters[namespace(Property.Attachments)]
export const getAttachmentFiles = (store: Store<State>): File[] => store.getters[namespace(Getter.AttachmentFiles)]
export const getInvalidAttachment = (store: Store<State>): Attachment | undefined => store.getters[namespace(Getter.GetInvalidAttachment)]
export const getEmptyAttachment = (store: Store<State>): Attachment | undefined => store.getters[namespace(Getter.GetEmptyAttachment)]
export const getCorruptAttachment = (store: Store<State>): Attachment | undefined => store.getters[namespace(Getter.GetCorruptAttachment)]
export const getTooManyAddresses = (store: Store<State>): boolean => store.getters[namespace(Getter.GetTooManyAddresses)]
export const getLoadingAttachmentIndexes = (store: Store<State>): number[] => store.getters[namespace(Getter.GetLoadingAttachmentIndexes)]
export const getAttachmentWithTooManyAddresses = (store: Store<State>): Attachment | undefined => store.getters[namespace(Getter.GetAttachmentWithTooManyAddresses)]
export const getValidationIsOngoing = (store: Store<State>): boolean => store.getters[namespace(Property.ValidationIsOngoing)]
export const getField = (store: Store<State>, formId: FormId, fieldName: keyof FormFields): string => {
  return store.getters[namespace(Getter.GetField)]({
    formId,
    fieldName
  })
}

/* istanbul ignore next */
const landLordOrderPropertyModule = {
  namespaced: true,
  state: {
    [Property.Forms]: [{
      id: 1,
      fields: {
        [PropertyFormFields.StreetName]: '',
        [PropertyFormFields.StreetNumber]: '',
        [PropertyFormFields.ApartmentNumber]: '',
        [PropertyFormFields.Entrance]: '',
        [PropertyFormFields.City]: '',
        [PropertyFormFields.TenantsName]: '',
        [PropertyFormFields.TenantsPhone]: '',
        [PropertyFormFields.SwitchLocation]: '',
        [PropertyFormFields.Bridge]: ''
      },
      valid: false
    }],
    [Property.Attachments]: [],
    [Property.ValidationIsOngoing]: false,
    [Property.IsFormDirty]: false
  },
  getters: {
    [Property.Forms]: (state: State): PropertyForm[] => state[Property.Forms],
    [Property.IsFormDirty]: (state: State): boolean => state[Property.IsFormDirty],
    [Property.Attachments]: (state: State): Attachment[] => state[Property.Attachments],
    [Property.ValidationIsOngoing]: (state: State): boolean => state[Property.ValidationIsOngoing],
    [Getter.Attachment]: (state: State) => (attachmentName: string): Attachment | undefined => {
      return state[Property.Attachments].find(attachment => attachment.file.name === attachmentName)
    },
    [Getter.GetField]: (state: State) => ({ formId, fieldName }: {formId: FormId; fieldName: keyof FormFields}) => {
      return findForm(state, formId).fields[fieldName]
    },
    [Getter.VisibleFormCount]: (state: State): number => state.forms.length,
    [Getter.AllFieldsAreValid]: (state: State): boolean => {
      const invalidForms = state.forms.filter((form: PropertyForm) => !form.valid)
      return !invalidForms.length
    },
    [Getter.AttachmentFiles]: (state: State): File[] => state[Property.Attachments].map(data => data.file),
    [Getter.GetInvalidAttachment]: (state: State): Attachment | undefined => state[Property.Attachments].find(attachment => attachment.invalidRows.length),
    [Getter.GetEmptyAttachment]: (state: State): Attachment | undefined => state[Property.Attachments].find(attachment => attachment.empty),
    [Getter.GetAttachmentWithTooManyAddresses]: (state: State): Attachment | undefined => {
      return state[Property.Attachments].find(data => data.tooManyAddresses)
    },
    [Getter.GetTooManyAddresses]: (state: State): boolean => {
      return state[Property.Attachments].reduce((count, attachment) => {
        return count + attachment.addresses.length
      }, 0) > ADDRESS_LIMIT
    },
    [Getter.GetCorruptAttachment]: (state: State): Attachment | undefined => {
      return state[Property.Attachments].find(data => data.corrupt)
    },
    [Getter.GetLoadingAttachmentIndexes]: (state: State): number[] => {
      return state[Property.Attachments].reduce((indexes, attachment, index) => {
        if (attachment.loading) { indexes.push(index) }
        return indexes
      }, [] as number[])
    }
  },
  mutations: {
    [Setter.UpdateField]: (state: State, { formId, fieldName, value }: { formId: FormId; fieldName: keyof FormFields; value: string }) => {
      return findForm(state, formId).fields[fieldName] = value
    },
    [Setter.CreateForm]: (state: State, formId: FormId) => {
      state.forms.push({
        id: formId,
        fields: {
          [PropertyFormFields.StreetName]: '',
          [PropertyFormFields.StreetNumber]: '',
          [PropertyFormFields.ApartmentNumber]: '',
          [PropertyFormFields.Entrance]: '',
          [PropertyFormFields.City]: '',
          [PropertyFormFields.TenantsName]: '',
          [PropertyFormFields.TenantsPhone]: '',
          [PropertyFormFields.SwitchLocation]: '',
          [PropertyFormFields.Bridge]: ''
        },
        valid: false
      })
    },
    [Setter.SetFormValid]: (state: State, { formId, valid }: { formId: FormId; valid: boolean }) => state.forms[formId - 1].valid = valid,
    [Setter.CopyFormValues]: (state: State, { from, to }: { from: FormId; to: FormId }) => {
      const fromForm = findForm(state, from)
      const toForm = findForm(state, to)

      toForm.fields = {
        ...fromForm.fields,
        [PropertyFormFields.TenantsName]: '',
        [PropertyFormFields.TenantsPhone]: '',
        [PropertyFormFields.SwitchLocation]: '',
        [PropertyFormFields.ApartmentNumber]: ''
      }
    },
    [Setter.RemoveForms]: (state: State, formId: FormId) => {
      if (formId) {
        state.forms = state.forms.filter(form => form.id !== formId)
      } else {
        state.forms = []
        state.forms.push({
          id: 1,
          fields: {
            [PropertyFormFields.StreetName]: '',
            [PropertyFormFields.StreetNumber]: '',
            [PropertyFormFields.ApartmentNumber]: '',
            [PropertyFormFields.Entrance]: '',
            [PropertyFormFields.City]: '',
            [PropertyFormFields.TenantsName]: '',
            [PropertyFormFields.TenantsPhone]: '',
            [PropertyFormFields.SwitchLocation]: '',
            [PropertyFormFields.Bridge]: ''
          },
          valid: false
        })
      }
    },
    [Setter.SetValidationIsOngoing]: (state: State, status: boolean) => state[Property.ValidationIsOngoing] = status,
    [Setter.AddAttachment]: (state: State, file: File) => state[Property.Attachments].push({
      file,
      fileName: file.name,
      addresses: [],
      invalidRows: [],
      corrupt: false,
      loading: true,
      empty: false,
      tooManyAddresses: false
    }),
    [Setter.SetAddresses]: (state: State, { addresses, attachmentName }: { addresses: ParsedAddressSpreadsheetData[]; attachmentName: string }) => {
      const attachmentIndex = findIndexByAttachmentName(state, attachmentName)
      state[Property.Attachments][attachmentIndex].addresses = addresses
    },
    [Setter.SetAttachmentEmpty]: (state: State, { empty, attachmentName }: { empty: boolean; attachmentName: string }) => {
      const attachmentIndex = findIndexByAttachmentName(state, attachmentName)
      state[Property.Attachments][attachmentIndex].empty = empty
    },
    [Setter.SetAttachmentCorrupt]: (state: State, { corrupt, attachmentName }: { corrupt: boolean; attachmentName: string }) => {
      const attachmentIndex = findIndexByAttachmentName(state, attachmentName)
      state[Property.Attachments][attachmentIndex].corrupt = corrupt
    },
    [Setter.SetAttachmentTooManyAddresses]: (state: State, { status, attachmentName }: { status: boolean; attachmentName: string }) => {
      const attachmentIndex = findIndexByAttachmentName(state, attachmentName)
      state[Property.Attachments][attachmentIndex].tooManyAddresses = status
    },
    [Setter.SetAttachmentBeingValidated]: (state: State, { attachmentName, loading }: {attachmentName: string; loading: boolean}) => {
      const attachmentIndex = findIndexByAttachmentName(state, attachmentName)
      state[Property.Attachments][attachmentIndex].loading = loading
    },
    [Setter.RemoveAttachment]: (state: State, attachmentName: string) => {
      if (attachmentName !== undefined) {
        const fileIndex = findIndexByAttachmentName(state, attachmentName)
        state[Property.Attachments].splice(fileIndex, 1)
      } else {
        state[Property.Attachments] = []
      }
    },
    [Setter.SetInvalidRows]: (state: State, { attachmentName, invalidRows }: {attachmentName: string; invalidRows: number[]}) => {
      const attachmentIndex = findIndexByAttachmentName(state, attachmentName)
      state[Property.Attachments][attachmentIndex].invalidRows = invalidRows
    },
    [Setter.UpdateIsFormDirty]: (state: State, payload: boolean) => state[Property.IsFormDirty] = payload
  },
  actions: {
    [Action.UpdateField]: ({ commit }: ActionContext, payload: { formId: FormId; fieldName: keyof FormFields; value: string }): void => {
      commit(Setter.UpdateField, payload)
      commit(Setter.UpdateIsFormDirty, true)
    },
    [Action.SetFormValid]: ({ commit }: ActionContext, payload: { formId: FormId; valid: boolean }): void => commit(Setter.SetFormValid, payload),
    [Action.CreateForm]: ({ commit }: ActionContext, formId: number): void => commit(Setter.CreateForm, formId),
    [Action.RemoveForms]: ({ commit }: ActionContext, formId: number): void => commit(Setter.RemoveForms, formId),
    [Action.CopyFormValues]: ({ commit }: ActionContext, payload: { from: FormId; to: FormId }): void => commit(Setter.CopyFormValues, payload),
    [Action.ResetPropertyForm]: ({ commit }: ActionContext): void => {
      commit(Setter.RemoveForms, null)
      commit(Setter.RemoveAttachment)
      commit(Setter.UpdateIsFormDirty, false)
    },
    [Action.SetValidationIsOngoing]: ({ commit }: ActionContext, val: boolean): void => {
      commit(Setter.SetValidationIsOngoing, val)
    },
    [Action.ValidateParsedAttachment]: async ({ commit, getters }: ActionContext, attachmentName: string ): Promise<void> => {
      commit(Setter.SetValidationIsOngoing, true)
      commit(Setter.SetAttachmentBeingValidated, { attachmentName, loading: true })
      const attachment: Attachment = getters[Getter.Attachment](attachmentName)

      try {
        if (isAttachmentEmpty(attachment)) {
          commit(Setter.SetAttachmentEmpty, { attachmentName, empty: true })
        } else if (tooManyAddresses(attachment)) {
          commit(Setter.SetAttachmentTooManyAddresses, { attachmentName, status: true })
        } else {
          const invalidEntries = validateRequiredFields(attachment.addresses)
          const invalidAddresses = await validateAddresses(attachment.addresses)
          const invalidRows = getSortedInvalidRows([...invalidEntries, ...invalidAddresses])
          commit(Setter.SetInvalidRows, { attachmentName, invalidRows })
        }
      } catch {
        commit(Setter.SetAttachmentCorrupt, { attachmentName, corrupt: true })
      } finally {
        commit(Setter.SetAttachmentBeingValidated, { attachmentName, loading: false })
        commit(Setter.SetValidationIsOngoing, false)
      }
    },
    [Action.ParseAddresses]: async ({ commit, getters }: ActionContext, attachmentName: string ): Promise<void> => {
      try {
        const attachment: Attachment = getters[Getter.Attachment](attachmentName)
        const addresses = await parseExcelSheetAddresses(attachment.file, SHEET_RANGE)

        commit(Setter.SetAddresses, { attachmentName, addresses })
      } catch {
        commit(Setter.SetAttachmentCorrupt, { attachmentName, corrupt: true })
      } finally {
        commit(Setter.SetAttachmentBeingValidated, { attachmentName, loading: false })
      }
    },
    [Action.AddAttachment]: async ({ commit, dispatch }: ActionContext, attachment: File): Promise<void> => {
      commit(Setter.RemoveForms)
      commit(Setter.AddAttachment, attachment)
      commit(Setter.UpdateIsFormDirty, true)

      const attachmentName = attachment.name
      await dispatch(Action.ParseAddresses, attachmentName)
      await dispatch(Action.ValidateParsedAttachment, attachmentName)
    },
    [Action.RemoveAttachment]: async ({ commit }: ActionContext, attachmentName: string): Promise<void> => {
      commit(Setter.RemoveAttachment, attachmentName)
    }
  }
}

export default landLordOrderPropertyModule
